import axios from "axios"

import {genericThunk} from "../../../actions/actionsUtils"
import {AUTH_ENDPOINTS, CONNECT_USER_ENDPOINT, PASSWORD_ENDPOINTS} from "../../../constants/endpoints"
import httpMethods from "../../../constants/httpMethods"
import {RequestInfo, RequestKeys} from "../../../constants/requests"
import {getErrorMessage} from "../../../services/api"
import {addTokenToHeader, removeTokenFromHeader} from "../../../services/configureAxios"
import {addTokenServiceAPIToHeader, removeTokenServiceAPIFromHeader} from "../../../services/configureServiceAPIAxios"
import * as sessionStorageService from "../../../services/sessionStorage"
import {REQUEST, REQUEST_FAILURE, REQUEST_SUCCESS} from "../../../store/requests/requestsReducer"
import {CommonRequestKeys} from "../../constants/requests"
import {
    CONNECT_USER_FAILURE,
    CONNECT_USER_REQUEST,
    CONNECT_USER_SUCCESS,
    DISCONNECT_USER,
    RECOVER_LOGIN_PASSWORD_FAILURE,
    RECOVER_LOGIN_PASSWORD_REQUEST,
    RECOVER_LOGIN_PASSWORD_SUCCESS,
    SET_BYPASSED_AUTHENTICATION,
} from "./userActionTypes"

/**
 * Thunk to connect a user.
 * Dispatch on request and on success or failure.
 * @param login the user name
 * @param password the user password
 * @param typeEspace the application area where you want to connect
 */
export const connectUser = (login, password, typeEspace) => dispatch => {
    dispatch({
        type: REQUEST,
        requestKeyName: CommonRequestKeys.USER_AUTHORITY,
    })

    // Build request body.
    const body = {
        login,
        password,
        typeEspace,
    }

    return axios.post(CONNECT_USER_ENDPOINT, body).then(
        response => {
            if (response.data.token) {
                addTokenToHeader(response.data.token)
                addTokenServiceAPIToHeader(response.data.token)
                dispatch({
                    type: REQUEST_SUCCESS,
                    requestKeyName: CommonRequestKeys.USER_AUTHORITY,
                    response: response.data,
                })
            }

            return Promise.resolve(response.data)
        },
        error => {
            dispatch({
                type: REQUEST_FAILURE,
                requestKeyName: CommonRequestKeys.USER_AUTHORITY,
                message: getErrorMessage(error),
            })
            removeTokenFromHeader()
            removeTokenServiceAPIFromHeader()
            return Promise.reject(error)
        },
    )
}

/**
 * Thunk to disconnect an user locally
 */
export const disconnectUser = () => dispatch => {
    dispatch({
        type: DISCONNECT_USER,
    })
    removeTokenFromHeader()
    removeTokenServiceAPIFromHeader()
    sessionStorageService.clearState()
    return Promise.resolve()
}

export const logoutUser = () => dispatch => {
    dispatch({
        type: REQUEST,
        requestKeyName: CommonRequestKeys.USER_LOGOUT,
    })

    const headers = {
        "X-AUTH-REFRESH": false,
    }

    return axios
        .post(AUTH_ENDPOINTS.logout, undefined, {headers})
        .then(response => {
            dispatch({
                type: REQUEST_SUCCESS,
                requestKeyName: CommonRequestKeys.USER_LOGOUT,
                response: response.data,
            })
            return Promise.resolve()
        })
        .catch(error => {
            dispatch({
                type: REQUEST_FAILURE,
                requestKeyName: CommonRequestKeys.USER_LOGOUT,
                message: getErrorMessage(error),
            })
            return Promise.reject(error)
        })
}

/**
 * Thunk to start the recover login/password process
 */
export const recoverLoginPassword = (typeEspace, login, siret, birthDate) => dispatch => {
    dispatch({
        type: RECOVER_LOGIN_PASSWORD_REQUEST,
    })

    const params = {
        typeEspace,
        login,
        siret,
        birthDate,
    }

    return axios.get(AUTH_ENDPOINTS.recoverLoginPassword, {params}).then(
        response => {
            const data = {
                clientType: response.data.typeClient,
                mailSentTo: response.data.destinataires,
            }
            dispatch({
                type: RECOVER_LOGIN_PASSWORD_SUCCESS,
                response: data,
            })

            return Promise.resolve(data)
        },
        error => {
            const message = getErrorMessage(error)
            dispatch({
                type: RECOVER_LOGIN_PASSWORD_FAILURE,
                message,
            })

            return Promise.reject(message)
        },
    )
}

/**
 * Change password with a reset password token
 * Used when user has forgotten his password
 * @param {*} newPassword
 * @param {*} resetPasswordToken
 * @param {*} passwordRequestKeyName Nécessaire pour Prestadom et les autres espaces qui utilisent des clés différentes
 */
export const changePassword = (newPassword, resetPasswordToken, passwordRequestKeyName) => dispatch => {
    dispatch({
        type: REQUEST,
        requestKeyName: passwordRequestKeyName,
    })

    const body = {
        nouveauMotDePasse: newPassword,
        resetPasswordToken,
    }

    return axios.post(PASSWORD_ENDPOINTS.changePassword, body).then(
        response => {
            dispatch({
                type: REQUEST_SUCCESS,
                requestKeyName: passwordRequestKeyName,
            })

            let promise
            if (response?.data) {
                dispatch({
                    type: REQUEST_SUCCESS,
                    requestKeyName: CommonRequestKeys.USER_AUTHORITY,
                    response: response.data,
                })

                promise = response.data
            }

            return Promise.resolve(promise)
        },
        error => {
            dispatch({
                type: REQUEST_FAILURE,
                requestKeyName: passwordRequestKeyName,
                message: getErrorMessage(error),
            })

            return Promise.reject(getErrorMessage(error))
        },
    )
}

/**
 * Change user password when he is connected
 * Thunk to give the possibility to a user to change his password into his authentication informations
 * @param oldPassword   oldPassword user
 * @param newPassword   newPassword user
 * @param newPasswordConfirmation   newPasswordConfirmation user
 *  */
export const changeActualPassword = (oldPassword, newPassword, newPasswordConfirmation) => dispatch => {
    dispatch({
        type: REQUEST,
        requestKeyName: CommonRequestKeys.CHANGE_ACTUAL_PASSWORD,
    })

    const body = {
        ancienMotDePasse: oldPassword,
        nouveauMotDePasse: newPassword,
        nouveauMotDePasseConfirmation: newPasswordConfirmation,
    }

    return axios.patch(PASSWORD_ENDPOINTS.changeActualPassword, body).then(
        () => {
            dispatch({
                type: REQUEST_SUCCESS,
                requestKeyName: CommonRequestKeys.CHANGE_ACTUAL_PASSWORD,
            })

            return Promise.resolve()
        },
        error => {
            const message = getErrorMessage(error)
            dispatch({
                type: REQUEST_FAILURE,
                requestKeyName: CommonRequestKeys.CHANGE_ACTUAL_PASSWORD,
                message,
            })

            return Promise.reject(message)
        },
    )
}

/**
 * Thunk to mimiq user connection.
 * Dispatch on request and on success or failure.
 * @param sourceId the ciphered user id
 * @param userLogin the ciphered user login
 * @param areaType the application area where you want to connect
 * @param bypassToken the token used to bypass the login
 */
export const bypassLogin = (sourceId, userLogin, bypassToken, areaType, preOrderSessionId) => dispatch => {
    dispatch({
        type: CONNECT_USER_REQUEST,
    })

    // Build request body.
    const body = {
        identifiantSource: sourceId,
        identifiantCible: userLogin,
        jetonConnexion: bypassToken,
        typeEspace: areaType,
        idSessionPreCommande: preOrderSessionId ? preOrderSessionId : null,
    }

    return axios.post(AUTH_ENDPOINTS.bypassLogin, body).then(
        response => {
            dispatch({
                type: CONNECT_USER_SUCCESS,
                response: response.data,
            })
            dispatch({
                type: SET_BYPASSED_AUTHENTICATION,
            })
            addTokenToHeader(response.data.token)
            addTokenServiceAPIToHeader(response.data.token)

            return Promise.resolve()
        },
        error => {
            const message = getErrorMessage(error)
            dispatch({
                type: CONNECT_USER_FAILURE,
                message,
            })
            removeTokenFromHeader()
            removeTokenServiceAPIFromHeader()

            return Promise.reject(message)
        },
    )
}

export const validateAccount = token => dispatch => {
    const body = {
        token: token,
    }

    return new Promise((resolve, reject) => {
        genericThunk(
            body,
            httpMethods.POST,
            dispatch,
            AUTH_ENDPOINTS.validateAccount,
            null,
            RequestInfo[RequestKeys.VALIDATE_ACCOUNT].name,
            res => res.data,
        )
            .then(response => resolve(response))
            .catch(error => reject(error))
    })
}
